import { useDeleteEvent } from '@hooks'
import { countLabel } from '@/utils/plural'
import { TripHelper } from '@lib/TripHelper'
import { TestIds } from '@/testIds'
import { getErrorMessage } from '@lib/apiError'
import { queryKeys } from '@lib/queryKeys'
import { useQueryClient } from '@tanstack/react-query'
import { Trip } from '@types'
import { GetEventResponse } from '@orbit/api-types'
import dayjs from 'dayjs'
import React, { useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
	CardBody,
	DayGroup,
	DayHeader,
	EventDeleteButton,
	EventInfo,
	EventListPanel,
	EventLocation,
	EventName,
	EventNoLocation,
	EventRow,
	EventTime,
	RemoveAllButton,
	RemoveAllConfirm,
	SmallGhostButton,
	StateParagraph,
	SuccessBanner,
	SuccessBannerText,
	SuccessCheckChip,
	SuccessHeadline,
	SuccessSub,
} from '../ItineraryImportPage.styles'
import { TripSummary } from '../types'
import CenteredState from './CenteredState'
import { Check, MapPinGlyph, RotateCcw, TrashGlyph } from './icons'

interface SuccessBodyProps {
	tripId: string
	createdEventIds: number[]
	events: GetEventResponse[]
	trip: TripSummary
}

const SuccessBody: React.FC<SuccessBodyProps> = ({ tripId, createdEventIds, events, trip }) => {
	const idSet = new Set(createdEventIds)
	const created = events.filter(e => idSet.has(e.id))
	const tripHelper = useMemo(
		// TripSummary is a structural subset of the full Trip the helper reads from.
		() => (trip.startDate ? new TripHelper(trip as unknown as Trip) : null),
		[trip],
	)

	const pinnedCount = created.filter(e => !!e.location).length

	const deleteEvent = useDeleteEvent()
	const queryClient = useQueryClient()
	// The itinerary's import banner counts this import's live events, so it
	// must see each removal — and drop the banner once none are left.
	const refreshImportList = () =>
		queryClient.invalidateQueries({ queryKey: queryKeys.itineraryImports(tripId) })
	const [pendingIds, setPendingIds] = useState<Set<number>>(() => new Set())
	const [confirmingRemoveAll, setConfirmingRemoveAll] = useState(false)
	const [removingAll, setRemovingAll] = useState(false)
	const cancelRemoveAllRef = useRef(false)

	const handleDelete = async (id: number) => {
		setPendingIds(prev => new Set(prev).add(id))
		try {
			await deleteEvent.mutateAsync({ tripId, eventId: String(id) })
			void refreshImportList()
		} catch (error) {
			toast.error(getErrorMessage(error, "Couldn't remove that event."))
			setPendingIds(prev => {
				const next = new Set(prev)
				next.delete(id)
				return next
			})
		}
	}

	// Delete one at a time rather than all at once: concurrent deletes contend on
	// the backend (each event re-syncs travel segments) and can stall the whole
	// batch. Sequential deletes also let rows disappear as progress, and Cancel
	// can stop the loop between events.
	const handleRemoveAll = async () => {
		cancelRemoveAllRef.current = false
		setRemovingAll(true)
		let failures = 0
		for (const ev of created) {
			if (cancelRemoveAllRef.current) break
			try {
				await deleteEvent.mutateAsync({ tripId, eventId: String(ev.id) })
			} catch {
				failures += 1
			}
		}
		setRemovingAll(false)
		setConfirmingRemoveAll(false)
		cancelRemoveAllRef.current = false
		void refreshImportList()
		if (failures > 0) {
			toast.error(`Couldn't remove ${failures} event${failures === 1 ? '' : 's'}. Please try again.`)
		}
	}

	const handleCancelRemoveAll = () => {
		if (removingAll) cancelRemoveAllRef.current = true
		else setConfirmingRemoveAll(false)
	}

	const groups = useMemo(() => {
		const byDay = new Map<number, GetEventResponse[]>()
		for (const ev of created) {
			const dayIdx = ev.tripDayIndex ?? 0
			if (!byDay.has(dayIdx)) byDay.set(dayIdx, [])
			byDay.get(dayIdx)!.push(ev)
		}
		const sortedDays = Array.from(byDay.keys()).sort((a, b) => a - b)
		return sortedDays.map(dayIdx => {
			const evs = byDay.get(dayIdx)!
			evs.sort((a, b) => {
				const at = a.startTime ?? '99:99'
				const bt = b.startTime ?? '99:99'
				return at.localeCompare(bt)
			})
			const dateStr = tripHelper?.calculateDateFromDayIndex(dayIdx) ?? ''
			const label = dateStr ? dayjs(dateStr).format('dddd, MMM D') : `Day ${dayIdx + 1}`
			return { dayIdx, label, events: evs }
		})
	}, [created, tripHelper])

	if (created.length === 0) {
		return (
			<CenteredState
				tone="blue"
				icon={<RotateCcw size={40} strokeWidth={1.8} />}
				headline="Import cleared"
			>
				<StateParagraph>
					Everything from this import was removed from your trip. You can import again whenever
					you're ready.
				</StateParagraph>
			</CenteredState>
		)
	}

	return (
		<CardBody>
			<SuccessBanner>
				<SuccessCheckChip>
					<Check size={22} strokeWidth={2.5} />
				</SuccessCheckChip>
				<SuccessBannerText>
					<SuccessHeadline>
						Added {countLabel(created.length, 'event')} to your trip
					</SuccessHeadline>
					<SuccessSub>
						{groups.length > 0 && (
							<>
								Across {countLabel(groups.length, 'day')} ·{' '}
							</>
						)}
						{pinnedCount} of {created.length} pinned to the map
					</SuccessSub>
				</SuccessBannerText>
				{(created.length > 1 || removingAll) &&
					(confirmingRemoveAll ? (
						<RemoveAllConfirm>
							<SmallGhostButton onClick={handleCancelRemoveAll}>Cancel</SmallGhostButton>
							<RemoveAllButton onClick={handleRemoveAll} disabled={removingAll}>
								{removingAll ? 'Removing…' : `Remove all ${created.length}`}
							</RemoveAllButton>
						</RemoveAllConfirm>
					) : (
						<RemoveAllButton onClick={() => setConfirmingRemoveAll(true)}>Remove all</RemoveAllButton>
					))}
			</SuccessBanner>
			<EventListPanel>
				{groups.map((g, gi) => (
					<DayGroup key={g.dayIdx} $first={gi === 0}>
						<DayHeader>{g.label}</DayHeader>
						{g.events.map((ev, ei) => (
							<EventRow
								key={ev.id}
								$first={ei === 0}
								$idx={ei}
								data-testid={TestIds.aiImport.reviewEvent(ev.name)}
							>
								<EventInfo>
									<EventName>{ev.name}</EventName>
									{ev.location ? (
										<EventLocation>
											<MapPinGlyph size={11} />
											{ev.location.fullText ?? ev.location.primaryText ?? ''}
										</EventLocation>
									) : (
										<EventNoLocation>Unable to automatically detect location</EventNoLocation>
									)}
								</EventInfo>
								<EventTime>{ev.startTime ?? '—'}</EventTime>
								<EventDeleteButton
									onClick={() => handleDelete(ev.id)}
									disabled={pendingIds.has(ev.id) || removingAll}
									aria-label={`Remove ${ev.name}`}
								>
									<TrashGlyph size={15} />
								</EventDeleteButton>
							</EventRow>
						))}
					</DayGroup>
				))}
			</EventListPanel>
		</CardBody>
	)
}

export default SuccessBody
